﻿import { FormGroup, Label, Col, Input, InputGroup, InputGroupText } from 'reactstrap';
import { useEffect, useMemo, useRef, useState } from 'react';
import useTheme from '../../hooks/useTheme';
// See dataTable.jsx — Vite pre-bundles react-windowed-select's CJS default
// without unwrapping, so we normalize here.
import WindowedSelectModule from 'react-windowed-select';
const WindowedSelect = WindowedSelectModule.default ?? WindowedSelectModule;
import SkeletonSelect from './skeletonSelect';
import debounce from 'lodash/debounce';
import DatePicker from './datePicker';
import DateRangePicker from './dateRangePicker';
import { customSelectTheme, selectStyle } from './selectStyling';

export const DateTimeInput = (props) => {
    const handleDateTimeChange = (value) => {
        if (props.onChange)
            props.onChange(value || "");
    }
    return (
        <DatePicker
            value={props.value}
            isClearable={props.isClearable ?? true}
            handleChange={handleDateTimeChange}
            disableWeekends={props.disableWeekends}
        />
    );
}

export const DateRangeInput = (props) => (
    <DateRangePicker
        value={props.value ?? props.defaultValue ?? { from: "", to: "" }}
        handleChange={(v) => props.onChange?.(v)}
        disableWeekends={props.disableWeekends}
        isClearable={props.isClearable ?? true}
    />
);


export const SelectInput = (props) => {
    const theme = useTheme();
    const controlledValue = props.value ?? props.defaultValue ?? null;

    if (!props.options)
        return <SkeletonSelect />;

    const handleChange = (option) => {
        if (props.onChange)
            props.onChange(option?.value);
    }

    return (
        <Col style={{ minWidth: '170px' }}>
            <WindowedSelect
                id={props.id}
                isSearchable={props.isSearchable || false}
                defaultValue={props.defaultValue}
                options={props.options}
                styles={selectStyle}
                classNamePrefix="data-select"
                theme={customSelectTheme(theme)}
                onChange={handleChange}
                value={controlledValue}
                menuPortalTarget={document.body}
                menuPosition="fixed"
                menuShouldBlockScroll={false}
                menuShouldScrollIntoView={false}
                placeholder={props.placeholder || `Select ${props.label}...`}
                isDisabled={props.isDisabled || false}
                isClearable={props.isClearable !== undefined ? props.isClearable : true}
            />
        </Col>
    )
}

export const SwitchInput = (props) => {
    return (
        <Input
            id={props.id}
            type="switch"
            checked={!!props.value}
            disabled={props.isDisabled}
            onChange={(e) => props.onChange(e.target.checked)}
        />
    );
};


export const TextInput = (props) => {
    const onChangeRef = useRef(props.onChange);
    useEffect(() => {
        onChangeRef.current = props.onChange;
    }, [props.onChange]);

    const [text, setText] = useState(props.value ?? "");

    useEffect(() => {
        setText(props.value ?? "");
    }, [props.value]);

    const debouncedCommit = useMemo(
        () =>
            debounce((value) => {
                onChangeRef.current?.(value);
            }, 500),
        []
    );

    useEffect(() => () => debouncedCommit.cancel(), [debouncedCommit]);

    return (
        <Input
            id={props.id}
            type="text"
            bsSize="sm"
            value={text}
            onChange={(e) => {
                setText(e.target.value);
                debouncedCommit(e.target.value);
            }}
        />
    );
};

export const NumberRangeInput = (props) => {
    return (<div />);
}

export const NumberInput = (props) => {
    const onChangeRef = useRef(props.onChange);
    useEffect(() => {
        onChangeRef.current = props.onChange;
    }, [props.onChange]);

    // Local text state so the input updates immediately, not 500ms later
    const [text, setText] = useState(
        props.value === null || props.value === undefined ? "" : String(props.value)
    );

    // Keep local text in sync if parent changes filter externally (reset, load from storage, etc.)
    useEffect(() => {
        const next = props.value === null || props.value === undefined ? "" : String(props.value);
        setText(next);
    }, [props.value]);

    const debouncedCommit = useMemo(
        () =>
            debounce((raw) => {
                if (!onChangeRef.current) return;

                // Treat empty as null
                if (raw === "") {
                    onChangeRef.current(null);
                    return;
                }

                // Convert at the end
                const n = Number(raw);
                onChangeRef.current(Number.isFinite(n) ? n : null);
            }, 500),
        []
    );

    useEffect(() => () => debouncedCommit.cancel(), [debouncedCommit]);

    const handleChange = (e) => {
        const raw = e.target.value; // keep as string
        setText(raw);
        debouncedCommit(raw);
    };

    const input = (
        <Input
            className={props.poundSign ? "text-end" : ""}
            type="number"
            step={props.step ?? "1"}
            value={text}
            onChange={handleChange}
        />
    );

    return props.poundSign ? (
        <InputGroup>
            <InputGroupText>£</InputGroupText>
            {input}
        </InputGroup>
    ) : (
        input
    );
};

export default function DataFilter(props) {
    return (
        <>
            {(props.advanced === undefined || props.advanced === true) &&
                <div className="w-100">
                    <FormGroup switch={props.type === 'switch'} row className="w-100 mx-2 d-flex flex-row align-items-center">
                        {props.label !== "" && <Label for={props.id} sm={3} className="text-start">{props.label}</Label>}
                        <Col sm={9}>
                            {props.type === "datetime" && <DateTimeInput {...props} />}
                            {props.type === "daterange" && <DateRangeInput {...props} />}
                            {props.type === "select" && <SelectInput {...props} />}
                            {props.type === "switch" && <SwitchInput {...props} />}
                            {props.type === "text" && <TextInput {...props} />}
                            {props.type === "numberrange" && <NumberRangeInput {...props} />}
                            {props.type === "number" && <NumberInput {...props} />}
                        </Col>
                    </FormGroup>
                </div>
            }
            {props.advanced === false && (
                <>
                    {props.type === "switch" ? (
                        // flex-grow so inline filters wrap cleanly to full
                        // width on mobile instead of overflowing the toolbar.
                        <FormGroup switch className="m-0" style={{ flex: '1 1 160px', minWidth: '160px' }}>
                            <SwitchInput {...props} />
                            <Label for={props.id} check className="mb-0" style={{ marginLeft: '1rem' }}>{props.label}</Label>
                        </FormGroup>
                    ) : (
                        <div style={{ flex: '1 1 160px', minWidth: '160px' }}>
                            {props.type === "datetime" && <DateTimeInput {...props} />}
                            {props.type === "daterange" && <DateRangeInput {...props} />}
                            {props.type === "select" && <SelectInput {...props} />}
                            {props.type === "text" && <TextInput {...props} />}
                            {props.type === "numberrange" && <NumberRangeInput {...props} />}
                            {props.type === "number" && <NumberInput {...props} />}
                        </div>
                    )}
                </>
            )}

        </>
    );
}